import { useState, useEffect, useRef } from "react";
import axios from "axios";
import StockItemSearch from "../components/StockItemSearch";
import SendToFinanceDialog from "../components/SendToFinanceDialog";
import LotSubmittedViewerDialog from "../components/LotSubmittedViewerDialog";
import { 
  Package, Plus, Search, Send, FileText, Upload, Download, 
  Eye, Edit, Trash2, Check, X, AlertTriangle, Building, Calendar,
  FileSearch
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PermButton from "@/components/PermButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import BookVehicleDialog, { BookVehicleButton } from "../components/BookVehicleDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { uploadWithReceipt } from "@/lib/bulkUploadReceipt";
import useAuthStore from "@/store/authStore";
import { withNumericInputProps } from "@/utils/numericInput";
import DateRangePicker from "@/components/DateRangePicker";
import { defaultMonthRange, resolveRangeBounds } from "@/lib/dispatchLotsDateRange";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  DRAFT: "bg-gray-100 text-gray-700",
  PENDING_FINANCE: "bg-yellow-100 text-yellow-700",
  DISPATCHED: "bg-green-100 text-green-700",
  // Feb 2026 — new lot status. Applied when the linked invoice was
  // soft-deleted from PLAYFIELD_DRAFT/PUSH_FAILED OR was Zoho-voided.
  // Both paths converge here so operators can find + recover these
  // lots in one place. Orange picked to stand apart from red
  // (CANCELLED, permanent) and yellow (PENDING_FINANCE, in-flight).
  FINANCE_REJECTED: "bg-orange-100 text-orange-700",
  CANCELLED: "bg-red-100 text-red-700"
};

const STATUS_LABELS = {
  DRAFT: "Draft",
  PENDING_FINANCE: "Pending Finance",
  DISPATCHED: "Dispatched",
  FINANCE_REJECTED: "Finance Rejected",
  CANCELLED: "Cancelled",
};

// Feb 2026 — R/Y/G availability + Target Dispatch Completion Date.
// See services/dispatch_lot_status.py — status vocab is GREEN/YELLOW/RED;
// target-date kind is SCHEDULED / ON_HAND / UNRESOLVED.
const AVAILABILITY_DOT_COLORS = {
  GREEN: "bg-green-500",
  YELLOW: "bg-yellow-500",
  RED: "bg-red-500",
};

const AvailabilityDot = ({ status }) => {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>;
  const dot = AVAILABILITY_DOT_COLORS[status] || "bg-zinc-300";
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`avail-dot-${status.toLowerCase()}`}>
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot}`} />
      <span className="text-xs uppercase text-muted-foreground">{status}</span>
    </span>
  );
};

// Feb 2026 R14+ (AC7) — forecast-link status pill. Three states:
//   • LINKED  — every linked forecast exists AND has quantity > 0
//   • DELETED — at least one line's linked forecast has quantity=0
//                (either explicit bulk-upload zero-out, safety-net,
//                or single-forecast soft delete) OR is superseded
//   • NONE    — no line has a `forecast_id`
const ForecastStatusBadge = ({ status }) => {
  if (!status || status === "NONE") {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-zinc-100 text-zinc-600 border border-zinc-300"
        data-testid="forecast-status-none"
        title="No forecast linked to any line item"
      >
        No Forecast
      </span>
    );
  }
  if (status === "DELETED") {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700 border border-red-300"
        data-testid="forecast-status-deleted"
        title="One or more linked forecasts have been zeroed (soft-deleted)"
      >
        Deleted
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-700 border border-green-300"
      data-testid="forecast-status-linked"
      title="Every line's forecast link is live"
    >
      Linked
    </span>
  );
};

const LineTargetDate = ({ contribution }) => {
  if (!contribution) return <span className="text-muted-foreground">—</span>;
  const { kind, date } = contribution;
  if (kind === "SCHEDULED" && date) return <span className="font-mono">{date}</span>;
  if (kind === "ON_HAND") return <span className="text-green-700 font-medium">On-hand</span>;
  if (kind === "NO_SCHEDULE") return <span className="text-amber-600">No schedule</span>;
  if (kind === "NO_FORECAST") return <span className="text-muted-foreground">No forecast</span>;
  return <span className="text-muted-foreground">—</span>;
};

// Feb 2026 — "Created At" column formatter. Date-only display
// ("21 Sep 2026"), house convention from PurchaseOrderDetailDialog —
// never a full timestamp.
const fmtCreatedAt = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const TargetDispatchCell = ({ lot }) => {
  const kind = lot?.target_dispatch_completion_kind;
  const date = lot?.target_dispatch_completion_date;
  const unresolved = lot?.unresolved_lines_count || 0;
  if (kind === "SCHEDULED" && date) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="font-mono">{date}</span>
        {unresolved > 0 && (
          <span className="text-red-600 text-xs" data-testid="lot-target-unresolved">
            + {unresolved} unresolved
          </span>
        )}
      </span>
    );
  }
  if (kind === "ON_HAND") return <span className="text-green-700 font-medium">On-hand</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <span>—</span>
      {unresolved > 0 && (
        <span className="text-red-600 text-xs" data-testid="lot-target-unresolved">
          {unresolved} unresolved
        </span>
      )}
    </span>
  );
};


const PAYMENT_TERMS = [
  { value: "DUE_ON_RECEIPT", label: "Due on Receipt", days: 0 },
  { value: "NET_7", label: "Net 7", days: 7 },
  { value: "NET_15", label: "Net 15", days: 15 },
  { value: "NET_30", label: "Net 30", days: 30 },
  { value: "NET_45", label: "Net 45", days: 45 },
  { value: "NET_60", label: "Net 60", days: 60 }
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh"
];

const DispatchLots = () => {
  const { hasRole, isMasterAdmin, token } = useAuthStore();
  const isAdmin = isMasterAdmin();
  const isDemandTeam = hasRole('DEMAND_PLANNER') || isAdmin;
  const isFinanceTeam = hasRole('FINANCE_VIEWER') || hasRole('finance_viewer') || isAdmin;
  
  const [lots, setLots] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [buyerSkus, setBuyerSkus] = useState([]);
  const [branches, setBranches] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");// Feb 2026 — lot-level Availability (R/Y/G) filter. Value in the Select
  // is "ALL" / "RED" / "YELLOW" / "GREEN"; the backend applies the filter
  // post-enrichment because `availability_status` is derived from live
  // fg_inventory and isn't stored on the lot document.
  const [availabilityFilter, setAvailabilityFilter] = useState("ALL");
  // Feb 2026 R14+ (AC7) — Forecast Status filter (NONE / LINKED /
  // DELETED). Aggregated across the lot's line items' `forecast_id`
  // state. Applied server-side alongside availability.
  const [forecastStatusFilter, setForecastStatusFilter] = useState("ALL");
  // Feb 2026 (Created-by filter) — lists everyone who has created
  // ≥1 dispatch lot. Populated from `/dispatch-lots-v2/creators`
  // on mount; "ALL" default matches today's unfiltered behaviour
  // exactly (AC3). No RBAC gating — filter is data-driven.
  const [createdByFilter, setCreatedByFilter] = useState("ALL");
  const [creators, setCreators] = useState([]);
  // Sep 2026 — date-range filter on lot's `created_at`, replacing the
  // old Month dropdown. Defaults to the actual first → last day of the
  // current calendar month; both bounds are independently editable via
  // the picker's Custom mode. `allDates` is the explicit
  // no-date-restriction toggle (the "All dates" button).
  const [dateRange, setDateRange] = useState(defaultMonthRange);
  const [allDates, setAllDates] = useState(false);
  const handleRangeChange = (v) => {
    setDateRange(v);
    setAllDates(false);
  };
  
  // Create Lot Dialog (Demand Team)
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [lotLines, setLotLines] = useState([{ buyer_sku_id: "", quantity: 1 }]);
  const [lotNotes, setLotNotes] = useState("");
  
  // Invoice Dialog (Finance Team) — Feb 2026: the standalone Invoice
  // button + dialog were retired. Post-send-to-finance invoicing is
  // now done exclusively via the Dispatch Invoice screen through the
  // lot's `linked_dispatch_invoice_id`. All state/handlers/markup
  // that used to live here have been removed.

  // Send-to-Finance Dialog (Feb 2026 — Demand/KAM-owned capture step
  // that replaced the bare status-flip button).
  const [showSendToFinance, setShowSendToFinance] = useState(false);
  const [sendToFinanceLot, setSendToFinanceLot] = useState(null);

  // Feb 2026 — Read-only viewer for what was already submitted to Finance.
  // Shown for lots at PENDING_FINANCE or later, sourced strictly from the
  // persisted `dispatch_header` (never re-derived from live prefill).
  const [showSubmittedViewer, setShowSubmittedViewer] = useState(false);
  const [submittedViewerLot, setSubmittedViewerLot] = useState(null);

  const fileInputRef = useRef(null);
  
  // Finance Create Lot Dialog
  const [showFinanceCreateDialog, setShowFinanceCreateDialog] = useState(false);
  const [financeCreateData, setFinanceCreateData] = useState({
    customer_id: "",
    branch_id: "",
    order_number: "",
    notes: ""
  });
  const [financeLines, setFinanceLines] = useState([{ buyer_sku_id: "", quantity: 1, rate: 0 }]);
  const [financeCreating, setFinanceCreating] = useState(false);
  
  // Add Line Dialog
  const [showAddLineDialog, setShowAddLineDialog] = useState(false);
  const [addLineData, setAddLineData] = useState({ buyer_sku_id: "", quantity: 1 });
  const [addLineLotId, setAddLineLotId] = useState(null);
  const [addingLine, setAddingLine] = useState(false);
  const [skuLookup, setSkuLookup] = useState(null);

  // View Lot dialog (Feb 2026) — read-only inspection of a dispatch lot's
  // header + line items, with "Add Line" promoted INSIDE the dialog (out
  // of the row Actions to declutter the table).
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewLot, setViewLot] = useState(null);
  const [bookVehicleOpen, setBookVehicleOpen] = useState(false);
  // Feb 2026 — inline qty edit + delete on DRAFT view. `editedLines`
  // shadows viewLot.lines so a mid-edit re-fetch doesn't clobber
  // in-progress user input. Dirty state = shallow compare of
  // [{buyer_sku_id, quantity}, …] against the freshly-loaded lot.
  const [editedLines, setEditedLines] = useState([]);
  const [savingLotEdits, setSavingLotEdits] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchLots();
    fetchSummary();
    fetchCustomers();
    fetchBuyerSkus();
    fetchBranches();
    fetchCreators();
  }, [statusFilter, customerFilter, availabilityFilter, forecastStatusFilter, createdByFilter, dateRange, allDates]);

  // Sep 2026 — the list AND the summary consume the exact same date
  // bounds so the header stat cards mirror the filtered table
  // row-for-row. `allDates` sends no date params at all.
  const appendDateParams = (params) => {
    const bounds = resolveRangeBounds(dateRange, allDates);
    if (bounds?.from) params.append("date_from", bounds.from);
    if (bounds?.to) params.append("date_to", bounds.to);
  };

  const fetchLots = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "ALL") params.append("status", statusFilter);
      if (customerFilter && customerFilter !== "ALL") params.append("customer_id", customerFilter);
      if (availabilityFilter && availabilityFilter !== "ALL") params.append("availability", availabilityFilter);
      if (forecastStatusFilter && forecastStatusFilter !== "ALL") params.append("forecast_status", forecastStatusFilter);
      if (createdByFilter && createdByFilter !== "ALL") params.append("created_by", createdByFilter);
      appendDateParams(params);

      const res = await axios.get(`${API}/api/dispatch-lots-v2?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLots(res.data.lots || []);
    } catch (err) {
      console.error("Error fetching lots:", err);
    }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      // Feb 2026 — pass the same filter set as `fetchLots` so the
      // header stat cards mirror the table row-for-row. Includes
      // `forecast_status` (AC7 — previously omitted, so the cards
      // silently disagreed with the list when the filter was active)
      // and `created_by` (AC6).
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "ALL") params.append("status", statusFilter);
      if (customerFilter && customerFilter !== "ALL") params.append("customer_id", customerFilter);
      if (availabilityFilter && availabilityFilter !== "ALL") params.append("availability", availabilityFilter);
      if (forecastStatusFilter && forecastStatusFilter !== "ALL") params.append("forecast_status", forecastStatusFilter);
      if (createdByFilter && createdByFilter !== "ALL") params.append("created_by", createdByFilter);
      appendDateParams(params);
      const qs = params.toString();
      const res = await axios.get(`${API}/api/dispatch-lots-v2/summary${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(res.data);
    } catch (err) {
      console.error("Error fetching summary:", err);
    }
  };

  const fetchCreators = async () => {
    try {
      const res = await axios.get(`${API}/api/dispatch-lots-v2/creators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreators(res.data.creators || []);
    } catch (err) {
      console.error("Error fetching lot creators:", err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/api/buyers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(res.data || []);
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  const fetchBuyerSkus = async () => {
    try {
      const res = await axios.get(`${API}/api/sku-management/buyer-skus?page_size=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuyerSkus(res.data.items || res.data.buyer_skus || []);
    } catch (err) {
      console.error("Error fetching SKUs:", err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data || []);
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };

  // Demand Team Actions
  const handleCreateLot = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer");
      return;
    }
    
    const validLines = lotLines.filter(l => l.buyer_sku_id && l.quantity > 0);
    if (validLines.length === 0) {
      toast.error("Please add at least one valid line item");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/api/dispatch-lots-v2`, {
        customer_id: selectedCustomer,
        lines: validLines,
        notes: lotNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Lot ${res.data.lot_number} created`);
      setShowCreateDialog(false);
      resetLotForm();
      fetchLots();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create lot");
    }
  };

  const handleSendToFinance = async (lotId) => {
    // Feb 2026 — the bare status-flip has been replaced by a dialog
    // owned by Demand/KAM. Find the lot object, open the dialog.
    const lot = lots.find((l) => l.id === lotId);
    if (!lot) return;
    setSendToFinanceLot(lot);
    setShowSendToFinance(true);
  };

  const handleSendToFinanceSent = () => {
    // Dialog fired the POST successfully — refresh lot list + summary.
    fetchLots();
    fetchSummary();
  };

  const openSubmittedViewer = (lot) => {
    // Feb 2026 — surface the read-only capture of `dispatch_header`.
    // Only meaningful when the lot has already been sent to finance
    // (status gating enforced at the button-render site below).
    setSubmittedViewerLot(lot);
    setShowSubmittedViewer(true);
  };

  const handleDeleteLot = async (lotId) => {
    if (!window.confirm("Are you sure you want to delete this lot?")) return;
    
    try {
      await axios.delete(`${API}/api/dispatch-lots-v2/${lotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lot deleted");
      fetchLots();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete lot");
    }
  };

  // ============ Finance Create Lot Functions ============
  
  const handleFinanceCreateLot = async () => {
    if (!financeCreateData.customer_id || !financeCreateData.branch_id) {
      toast.error("Please select customer and branch");
      return;
    }
    
    const validLines = financeLines.filter(l => l.buyer_sku_id && l.quantity > 0);
    if (validLines.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }
    
    setFinanceCreating(true);
    try {
      const res = await axios.post(`${API}/api/dispatch-lots-v2/finance/create-lot`, {
        customer_id: financeCreateData.customer_id,
        branch_id: financeCreateData.branch_id,
        lines: validLines.map(l => ({
          buyer_sku_id: l.buyer_sku_id,
          quantity: parseInt(l.quantity),
          rate: l.rate || undefined
        })),
        order_number: financeCreateData.order_number || undefined,
        notes: financeCreateData.notes || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Dispatch lot ${res.data.lot_number} created`);
      setShowFinanceCreateDialog(false);
      setFinanceCreateData({ customer_id: "", branch_id: "", order_number: "", notes: "" });
      setFinanceLines([{ buyer_sku_id: "", quantity: 1, rate: 0 }]);
      fetchLots();
      fetchSummary();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_INVENTORY") {
        toast.error(`Insufficient inventory: ${detail.shortages?.length} item(s) short`);
      } else {
        toast.error(typeof detail === 'string' ? detail : "Failed to create lot");
      }
    } finally {
      setFinanceCreating(false);
    }
  };

  const handleFinanceLineSkuChange = async (index, skuId) => {
    const newLines = [...financeLines];
    newLines[index].buyer_sku_id = skuId;
    
    // Lookup SKU details
    if (skuId && financeCreateData.customer_id) {
      try {
        const res = await axios.get(
          `${API}/api/dispatch-lots-v2/sku-lookup/${skuId}?customer_id=${financeCreateData.customer_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        newLines[index].rate = res.data.rate || 0;
        newLines[index].sku_name = res.data.name;
        newLines[index].hsn_code = res.data.hsn_code;
        newLines[index].gst_rate = res.data.gst_rate;
      } catch (err) {
        // SKU not found or error - just update the ID
      }
    }
    
    setFinanceLines(newLines);
  };

  const addFinanceLine = () => {
    setFinanceLines([...financeLines, { buyer_sku_id: "", quantity: 1, rate: 0 }]);
  };

  const removeFinanceLine = (index) => {
    if (financeLines.length > 1) {
      setFinanceLines(financeLines.filter((_, i) => i !== index));
    }
  };

  // ============ Add Line to Existing Lot Functions ============
  
  const openAddLineDialog = (lot) => {
    setAddLineLotId(lot.id);
    setAddLineData({ buyer_sku_id: "", quantity: 1 });
    setSkuLookup(null);
    setShowAddLineDialog(true);
  };

  // Open the View Lot dialog. Loads the freshest copy of the lot via the
  // V2 detail endpoint so the embedded `lines[]` reflect everything
  // already added (avoids a stale render when add-line was just called).
  const openViewDialog = async (lot) => {
    setShowViewDialog(true);
    setViewLot(lot);   // optimistic render with what we already have
    setEditedLines(lot.lines ? lot.lines.map(l => ({ ...l })) : []);
    try {
      const res = await axios.get(`${API}/api/dispatch-lots-v2/${lot.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewLot(res.data);
      setEditedLines((res.data.lines || []).map(l => ({ ...l })));
    } catch (_e) {
      // Keep the optimistic copy; toast not needed — dialog still works.
    }
  };

  const refreshViewLot = async (lotId) => {
    if (!lotId) return;
    try {
      const res = await axios.get(`${API}/api/dispatch-lots-v2/${lotId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewLot(res.data);
      setEditedLines((res.data.lines || []).map(l => ({ ...l })));
    } catch (_e) { /* noop */ }
  };

  // Feb 2026 — inline qty edit + delete handlers. All three go through
  // the existing `PUT /{lot_id}` full-lines-replacement endpoint —
  // zero backend change per this ticket's scope.
  const isDraftEditable = viewLot?.status === "DRAFT";

  const isViewLotDirty = (() => {
    if (!viewLot || !isDraftEditable) return false;
    const orig = viewLot.lines || [];
    if (orig.length !== editedLines.length) return true;
    // Compare in order — we don't reorder rows on delete, so index
    // alignment holds until deletion. On deletion `editedLines.length`
    // already differs, so we short-circuited above.
    for (let i = 0; i < orig.length; i++) {
      if (orig[i].buyer_sku_id !== editedLines[i].buyer_sku_id) return true;
      if (Number(orig[i].quantity) !== Number(editedLines[i].quantity)) return true;
    }
    return false;
  })();

  const handleUpdateLineQty = (idx, rawValue) => {
    const parsed = parseInt(rawValue, 10);
    const nextQty = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    setEditedLines(prev => prev.map((l, i) => (i === idx ? { ...l, quantity: nextQty } : l)));
  };

  const handleDeleteLine = (idx) => {
    // AC3 guard — the client blocks removing the last line. This is
    // reinforced at save-time and again by the backend schema.
    setEditedLines(prev => {
      if (prev.length <= 1) {
        toast.error("A lot must have at least one line item — delete the whole lot from the list instead.");
        return prev;
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleDiscardLotEdits = () => {
    setEditedLines((viewLot?.lines || []).map(l => ({ ...l })));
  };

  const handleSaveLotEdits = async () => {
    if (!viewLot) return;
    if (editedLines.length === 0) {
      toast.error("A lot must have at least one line item");
      return;
    }
    setSavingLotEdits(true);
    try {
      const payload = {
        customer_id: viewLot.customer_id,
        forecast_id: viewLot.forecast_id || null,
        notes: viewLot.notes || null,
        lines: editedLines.map(l => ({
          buyer_sku_id: l.buyer_sku_id,
          quantity: Number(l.quantity) || 1,
          forecast_id: l.forecast_id || null,
        })),
      };
      await axios.put(`${API}/api/dispatch-lots-v2/${viewLot.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lot updated");
      await refreshViewLot(viewLot.id);
      fetchLots();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save changes");
    } finally {
      setSavingLotEdits(false);
    }
  };

  const handleAddLineLookup = async (skuId) => {
    setAddLineData({ ...addLineData, buyer_sku_id: skuId });
    
    if (!skuId) {
      setSkuLookup(null);
      return;
    }
    
    // Find the lot to get customer_id
    const lot = lots.find(l => l.id === addLineLotId);
    if (!lot) return;
    
    try {
      const res = await axios.get(
        `${API}/api/dispatch-lots-v2/sku-lookup/${skuId}?customer_id=${lot.buyer_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSkuLookup(res.data);
    } catch (err) {
      setSkuLookup(null);
      toast.error("SKU not found");
    }
  };

  const handleAddLineSubmit = async () => {
    if (!addLineData.buyer_sku_id || addLineData.quantity <= 0) {
      toast.error("Please enter valid SKU and quantity");
      return;
    }
    
    setAddingLine(true);
    try {
      const res = await axios.post(`${API}/api/dispatch-lots-v2/${addLineLotId}/add-line`, {
        buyer_sku_id: addLineData.buyer_sku_id,
        quantity: parseInt(addLineData.quantity)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Added ${res.data.sku_name} (${res.data.quantity} units)`);
      setShowAddLineDialog(false);
      fetchLots();
      
      // If View dialog is open for this lot, refresh it.
      if (showViewDialog && viewLot?.id === addLineLotId) {
        refreshViewLot(addLineLotId);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_INVENTORY") {
        toast.error(`Insufficient inventory: ${detail.shortage} units short`);
      } else {
        toast.error(typeof detail === 'string' ? detail : "Failed to add line");
      }
    } finally {
      setAddingLine(false);
    }
  };

  // Finance Team Actions
  // Feb 2026 — the standalone Invoice-from-lot flow (openInvoiceDialog /
  // checkInventory / handleCreateInvoice / resetInvoiceForm) was
  // retired. It wrote its own `invoice_data` blob onto the lot and
  // decremented `branch_inventory` directly, entirely bypassing the
  // real `dispatch_invoices` PLAYFIELD_DRAFT lifecycle that
  // `send-to-finance` creates. Post-send-to-finance invoicing is now
  // reached from the Submitted Dispatch Data viewer's "Continue to
  // Dispatch Invoice" button, which deep-links into the Dispatch
  // Invoice screen against `lot.linked_dispatch_invoice_id`.

  // Aug 2026 — handleMarkDispatched removed together with its backend
  // endpoint (POST /dispatch-lots-v2/{id}/dispatch) per Dispatch Lots
  // fix ticket AC1. DISPATCHED is set exclusively by the Zoho-approval
  // webhook via _mirror_lot_status(fresh, "DISPATCHED").

  // Bulk Upload
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const data = await uploadWithReceipt(`${API}/api/dispatch-lots-v2/bulk-upload`, formData, {
        axiosConfig: { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } },
        showToast: false,
      });
      
      toast.success((data.message || "Upload complete") + " — Receipt downloaded.");
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} errors occurred — see receipt for details.`);
      }
      fetchLots();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    
    e.target.value = null;
  };

  const downloadTemplate = () => {
    const template = [
      { customer_id: "CUST_0001", buyer_sku_id: "ERW001_TVS", quantity: 100 },
      { customer_id: "CUST_0001", buyer_sku_id: "ERW002_TVS", quantity: 50 },
      { customer_id: "CUST_0002", buyer_sku_id: "TRK001_HERO", quantity: 75 }
    ];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dispatch Lots");
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "dispatch_lots_template.xlsx");
  };

  const resetLotForm = () => {
    setSelectedCustomer("");
    setLotLines([{ buyer_sku_id: "", quantity: 1 }]);
    setLotNotes("");
  };

  return (
    <div className="p-6 md:p-8" data-testid="dispatch-lots-page">
      {/* Header */}
      <div className="mb-8 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase">Dispatch Lots</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            {isDemandTeam && "Create dispatch requests"} {isDemandTeam && isFinanceTeam && "•"} {isFinanceTeam && "Process invoices"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDemandTeam && (
            <>
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Template
              </Button>
              <input type="file" ref={fileInputRef} onChange={handleBulkUpload} accept=".xlsx,.xls" className="hidden" />
              <PermButton entity="DispatchLot" action="CREATE" variant="outline" onClick={() => fileInputRef.current.click()} data-testid="bulk-upload-lots-btn">
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </PermButton>
              <PermButton entity="DispatchLot" action="CREATE" onClick={() => setShowCreateDialog(true)} data-testid="create-lot-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Lot
              </PermButton>
            </>
          )}
          {isFinanceTeam && !isDemandTeam && (
            <PermButton entity="DispatchLot" action="CREATE" onClick={() => setShowFinanceCreateDialog(true)} data-testid="finance-create-lot-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Dispatch Lot
            </PermButton>
          )}
          {/* Aug 2026 — "Quick Invoice Lot" removed per Dispatch Lots
              fix ticket AC9: it was never a distinct feature, just the
              same create-lot dialog surfaced twice for dual-role users.
              Finance users with both roles still see the regular
              "Create Dispatch Lot" button above via the sibling
              !isDemandTeam branch and the isDemandTeam-only "New Lot"
              button. */}
        </div>
      </div>

      {/* Summary Cards — Sep 2026: total quantity + total value (Pulse's
          own valuation) at the TOP-LEFT of each tile; the existing lot
          count follows below them (AC1). */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        {[
          { key: "total", label: "Total Lots", cls: "bg-white", num: "" },
          { key: "DRAFT", label: "Draft", cls: "bg-gray-50", num: "text-gray-700" },
          { key: "PENDING_FINANCE", label: "Pending Finance", cls: "bg-yellow-50", num: "text-yellow-700" },
          { key: "DISPATCHED", label: "Dispatched", cls: "bg-green-50", num: "text-green-700" },
          { key: "FINANCE_REJECTED", label: "Finance Rejected", cls: "bg-orange-50", num: "text-orange-700" },
        ].map(({ key, label, cls, num }) => {
          const tile = (summary.tiles && summary.tiles[key]) || {};
          const count = tile.count ?? (key === "total" ? summary.total : summary[key]) ?? 0;
          const slug = key.toLowerCase();
          return (
            <div
              key={key}
              className={`${cls} border rounded-lg p-4 ${key === "FINANCE_REJECTED" ? "cursor-pointer hover:bg-orange-100 transition-colors" : ""}`}
              onClick={key === "FINANCE_REJECTED" ? () => setStatusFilter("FINANCE_REJECTED") : undefined}
              title={key === "FINANCE_REJECTED" ? "Click to filter by Finance Rejected" : undefined}
              data-testid={key === "FINANCE_REJECTED" ? "summary-card-finance-rejected" : `summary-card-${slug}`}
            >
              <div className="text-left text-xs text-muted-foreground mb-2">
                <div data-testid={`summary-qty-${slug}`}>
                  Qty: {Number(tile.quantity || 0).toLocaleString("en-IN")} units
                </div>
                <div data-testid={`summary-value-${slug}`}>
                  ₹ {Math.round(Number(tile.value || 0)).toLocaleString("en-IN")}
                </div>
              </div>
              <div
                className={`text-2xl font-bold ${num}`}
                data-testid={key === "FINANCE_REJECTED" ? "summary-count-finance-rejected" : `summary-count-${slug}`}
              >
                {count}
              </div>
              <div className="text-xs text-muted-foreground uppercase">{label}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="PENDING_FINANCE">Pending Finance</SelectItem>
            <SelectItem value="DISPATCHED">Dispatched</SelectItem>
            <SelectItem value="FINANCE_REJECTED">Finance Rejected</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={customerFilter} onValueChange={setCustomerFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Customers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Customers</SelectItem>
            {customers.slice(0, 100).map(c => (
              <SelectItem key={c.id} value={c.customer_code || c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
          <SelectTrigger className="w-[180px]" data-testid="filter-availability">
            <SelectValue placeholder="All Availability" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" data-testid="filter-availability-opt-ALL">All Availability</SelectItem>
            <SelectItem value="RED" data-testid="filter-availability-opt-RED">Red</SelectItem>
            <SelectItem value="YELLOW" data-testid="filter-availability-opt-YELLOW">Yellow</SelectItem>
            <SelectItem value="GREEN" data-testid="filter-availability-opt-GREEN">Green</SelectItem>
          </SelectContent>
        </Select>

        {/* Feb 2026 R14+ (AC7) — Forecast Status filter. Aggregates
            across the lot's line items' `forecast_id` state. */}
        <Select value={forecastStatusFilter} onValueChange={setForecastStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="filter-forecast-status">
            <SelectValue placeholder="All Forecast" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" data-testid="filter-forecast-status-opt-ALL">All Forecast</SelectItem>
            <SelectItem value="LINKED" data-testid="filter-forecast-status-opt-LINKED">Linked</SelectItem>
            <SelectItem value="DELETED" data-testid="filter-forecast-status-opt-DELETED">Deleted</SelectItem>
            <SelectItem value="NONE" data-testid="filter-forecast-status-opt-NONE">No Forecast</SelectItem>
          </SelectContent>
        </Select>

        {/* Feb 2026 (Created-by filter) — lists every person who has
            created ≥1 dispatch lot. Populated from the server's
            distinct set (see `/dispatch-lots-v2/creators`) so a new
            KAM appears the first time they save a lot, no separate
            setup step. "All KAMs" default matches today's unfiltered
            behaviour exactly (AC3). */}
        <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
          <SelectTrigger className="w-[180px]" data-testid="filter-created-by">
            <SelectValue placeholder="All KAMs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" data-testid="filter-created-by-opt-ALL">All KAMs</SelectItem>
            {creators.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`filter-created-by-opt-${c.id}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sep 2026 — Date-range filter on lot's `created_at`,
            replacing the Month dropdown. Defaults to the current
            calendar month; both bounds editable via the picker's
            Custom mode. The "All dates" toggle opts out of any date
            restriction. The shared DateRangePicker is untouched — the
            toggle is Dispatch-Lots-only. */}
        <div
          className={allDates ? "opacity-40 pointer-events-none" : ""}
          data-testid="filter-date-range"
        >
          <DateRangePicker value={dateRange} onChange={handleRangeChange} />
        </div>
        <Button
          variant={allDates ? "default" : "outline"}
          size="sm"
          className="h-9"
          onClick={() => setAllDates((v) => !v)}
          data-testid="filter-all-dates"
        >
          All dates
        </Button>
      </div>

      {/* Lots Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead>Lot #</TableHead>
              <TableHead className="w-[160px] max-w-[160px]">Customer</TableHead>
              <TableHead className="text-right">Total Qty</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Availability</TableHead>
              {/* Feb 2026 R14+ (AC7) — Forecast Status column replaces
                  the old "Items" column. Shows one of NONE / LINKED /
                  DELETED aggregated across the lot's line items. */}
              <TableHead data-testid="lot-forecast-status-col">Forecast</TableHead>
              <TableHead>Target Dispatch</TableHead>
              {/* Feb 2026 — Created At (date only, e.g. "21 Sep 2026"),
                  immediately before Created by. */}
              <TableHead data-testid="lot-created-at-col">Created At</TableHead>
              <TableHead data-testid="lot-created-by-col">Created by</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lots.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                  No dispatch lots found
                </TableCell>
              </TableRow>
            ) : (
              lots.map(lot => (
                <TableRow key={lot.id}>
                  <TableCell className="font-mono font-medium">{lot.lot_number}</TableCell>
                  {/* Feb 2026 — narrowed, wrapping customer cell: long
                      names wrap onto multiple lines within 160px instead
                      of overflowing the row. */}
                  <TableCell className="w-[160px] max-w-[160px] whitespace-normal break-words leading-snug">{lot.customer_name}</TableCell>
                  <TableCell className="text-right font-mono">{lot.total_quantity}</TableCell>
                  <TableCell>
                    <Badge
                      className={STATUS_COLORS[lot.status]}
                      title={
                        lot.status === "FINANCE_REJECTED"
                          ? `Finance Rejected — ${lot.finance_rejected_reason || "the previous invoice was deleted or voided"}. Click Resubmit to Finance to send a fresh capture.`
                          : undefined
                      }
                      data-testid={`lot-status-badge-${lot.id}`}
                    >
                      {STATUS_LABELS[lot.status] || lot.status}
                    </Badge>
                  </TableCell>
                  <TableCell data-testid={`lot-avail-${lot.id}`}>
                    <AvailabilityDot status={lot.availability_status} />
                  </TableCell>
                  <TableCell data-testid={`lot-forecast-status-${lot.id}`}>
                    <ForecastStatusBadge status={lot.forecast_status} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground" data-testid={`lot-target-${lot.id}`}>
                    <TargetDispatchCell lot={lot} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-testid={`lot-created-at-${lot.id}`}>
                    {fmtCreatedAt(lot.created_at)}
                  </TableCell>
                  <TableCell className="text-sm" data-testid={`lot-created-by-${lot.id}`}>
                    {lot.created_by_name || "—"}
                  </TableCell>
                  <TableCell>
                    {/* Actions left-aligned within the column to hug the
                        header text and remove the dead space between this
                        column and the previous one. */}
                    <div className="flex items-center justify-start gap-1 flex-nowrap">
                      {/* Aug 2026 — "View Lot Details" (eye icon) is
                          the DRAFT lot's line-item editor. Restricted
                          to DRAFT only per ticket AC6 — non-DRAFT
                          lots use the read-only submitted-finance
                          viewer (FileSearch icon) below instead. */}
                      {lot.status === "DRAFT" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openViewDialog(lot)}
                          title="View lot details"
                          data-testid={`lot-view-${lot.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {["PENDING_FINANCE", "DISPATCHED", "RECEIVED", "CLOSED"].includes(lot.status) && lot.dispatch_header && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openSubmittedViewer(lot)}
                          title="View submitted finance data (read-only)"
                          data-testid={`lot-view-submitted-${lot.id}`}
                        >
                          <FileSearch className="h-4 w-4 text-indigo-600" />
                        </Button>
                      )}
                      {lot.status === "DRAFT" && isDemandTeam && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleSendToFinance(lot.id)} title="Send to Finance" data-testid={`s2f-open-${lot.id}`}>
                            <Send className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteLot(lot.id)} title="Delete">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                      {lot.status === "FINANCE_REJECTED" && isDemandTeam && (
                        // AC4 — recovery flow: resubmit reopens the
                        // SendToFinanceDialog on the SAME lot (see
                        // dispatch_lots_v2_routes.send_to_finance,
                        // which accepts DRAFT + FINANCE_REJECTED and
                        // writes directly onto the same lot document —
                        // no separate copy, no drift).
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSendToFinance(lot.id)}
                          title={`Resubmit to Finance — ${lot.finance_rejected_reason || "previous invoice was deleted or voided"}`}
                          data-testid={`s2f-resubmit-${lot.id}`}
                        >
                          <Send className="h-4 w-4 text-orange-600" />
                        </Button>
                      )}
                      {/* Aug 2026 — the INVOICED-gated manual "Dispatch"
                          button was removed together with its backend
                          endpoint (POST /dispatch-lots-v2/{id}/dispatch)
                          per ticket AC1/AC2. DISPATCHED is now reachable
                          exclusively through the Zoho-approval webhook
                          → _mirror_lot_status(fresh, "DISPATCHED"). */}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Lot Dialog - Demand Team */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetLotForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase flex items-center gap-2">
              <Package className="h-5 w-5" />
              Create Dispatch Lot
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.customer_code || c.id}>
                      {c.name} ({c.customer_code || c.id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Line Items *</Label>
                <Button size="sm" variant="outline" onClick={() => setLotLines([...lotLines, { buyer_sku_id: "", quantity: 1 }])}>
                  <Plus className="h-3 w-3 mr-1" /> Add Line
                </Button>
              </div>
              
              <div className="space-y-2">
                {lotLines.map((line, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <div className="flex-1">
                      <StockItemSearch
                        searchEndpoint={
                          selectedCustomer
                            ? `${process.env.REACT_APP_BACKEND_URL}/api/dispatch-lots-v2/sku-search?customer_id=${encodeURIComponent(selectedCustomer)}`
                            : `${process.env.REACT_APP_BACKEND_URL}/api/dispatch-lots-v2/sku-search?customer_id=__none__`
                        }
                        disabled={!selectedCustomer}
                        value={line.buyer_sku_id}
                        onSelect={(item) => {
                          const updated = [...lotLines];
                          updated[idx].buyer_sku_id = item.item_id;
                          setLotLines(updated);
                        }}
                        excludeIds={lotLines.filter((l, i) => i !== idx && l.buyer_sku_id).map(l => l.buyer_sku_id)}
                        placeholder={selectedCustomer ? "Type SKU ID or name..." : "Select a customer first"}
                        testId={`lot-sku-search-${idx}`}
                      />
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={line.quantity}
                      onChange={(e) => {
                        const updated = [...lotLines];
                        updated[idx].quantity = parseInt(e.target.value) || 1;
                        setLotLines(updated);
                      }}
                      {...withNumericInputProps("w-24 text-right")}
                      placeholder="Qty"
                    />
                    {lotLines.length > 1 && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setLotLines(lotLines.filter((_, i) => i !== idx))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={lotNotes} 
                onChange={(e) => setLotNotes(e.target.value)}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateLot}>Create Lot</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Invoice Dialog retired. The standalone parallel
          mechanism that wrote `dispatch_lots.invoice_data` and
          decremented `branch_inventory` directly was removed; Finance
          now finishes each invoice on the Dispatch Invoice screen via
          `lot.linked_dispatch_invoice_id` (reached from the read-only
          viewer's Continue-to-Dispatch-Invoice button). */}


      {/* Finance Create Dispatch Lot Dialog */}
      <Dialog open={showFinanceCreateDialog} onOpenChange={setShowFinanceCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create Dispatch Lot (Finance)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer *</Label>
                <Select 
                  value={financeCreateData.customer_id} 
                  onValueChange={(v) => setFinanceCreateData({ ...financeCreateData, customer_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.customer_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Branch (for inventory) *</Label>
                <Select 
                  value={financeCreateData.branch_id} 
                  onValueChange={(v) => setFinanceCreateData({ ...financeCreateData, branch_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Order Number</Label>
                <Input 
                  placeholder="PO-12345"
                  value={financeCreateData.order_number}
                  onChange={(e) => setFinanceCreateData({ ...financeCreateData, order_number: e.target.value })}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Input 
                  placeholder="Optional notes"
                  value={financeCreateData.notes}
                  onChange={(e) => setFinanceCreateData({ ...financeCreateData, notes: e.target.value })}
                />
              </div>
            </div>
            
            <Separator />
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Line Items *</Label>
                <Button size="sm" variant="outline" onClick={addFinanceLine}>
                  <Plus className="w-4 h-4 mr-1" /> Add Line
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[300px]">SKU</TableHead>
                      <TableHead className="w-[100px]">Qty</TableHead>
                      <TableHead className="w-[120px]">Rate</TableHead>
                      <TableHead className="w-[100px]">HSN</TableHead>
                      <TableHead className="w-[80px]">GST %</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {financeLines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <StockItemSearch
                            searchEndpoint={
                              financeCreateData.customer_id
                                ? `${process.env.REACT_APP_BACKEND_URL}/api/dispatch-lots-v2/sku-search?customer_id=${encodeURIComponent(financeCreateData.customer_id)}`
                                : `${process.env.REACT_APP_BACKEND_URL}/api/dispatch-lots-v2/sku-search?customer_id=__none__`
                            }
                            disabled={!financeCreateData.customer_id}
                            value={line.buyer_sku_id}
                            onSelect={(item) => handleFinanceLineSkuChange(idx, item.item_id)}
                            excludeIds={financeLines.filter((l, i) => i !== idx && l.buyer_sku_id).map(l => l.buyer_sku_id)}
                            placeholder={financeCreateData.customer_id ? "Type SKU ID or name..." : "Select a customer first"}
                            testId={`finance-sku-search-${idx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            min="1"
                            value={line.quantity}
                            onChange={(e) => {
                              const newLines = [...financeLines];
                              newLines[idx].quantity = parseInt(e.target.value) || 1;
                              setFinanceLines(newLines);
                            }}
                            {...withNumericInputProps()}
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            min="0"
                            step="0.01"
                            value={line.rate || ""}
                            placeholder="Auto"
                            onChange={(e) => {
                              const newLines = [...financeLines];
                              newLines[idx].rate = parseFloat(e.target.value) || 0;
                              setFinanceLines(newLines);
                            }}
                            {...withNumericInputProps()}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {line.hsn_code || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {line.gst_rate || 18}%
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => removeFinanceLine(idx)}
                            disabled={financeLines.length === 1}
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              <p className="text-xs text-muted-foreground mt-2">
                Rate, HSN, and GST will auto-populate from master data. Inventory will be validated at selected branch.
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowFinanceCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleFinanceCreateLot} disabled={financeCreating}>
                {financeCreating ? "Creating..." : "Create Lot"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Line Item Dialog */}
      <Dialog open={showAddLineDialog} onOpenChange={setShowAddLineDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Line Item
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>SKU *</Label>
              {(() => {
                const addLineLot = lots.find(l => l.id === addLineLotId);
                const lotCustomerId = addLineLot?.buyer_id || addLineLot?.customer_id || "";
                return (
                  <StockItemSearch
                    searchEndpoint={
                      lotCustomerId
                        ? `${process.env.REACT_APP_BACKEND_URL}/api/dispatch-lots-v2/sku-search?customer_id=${encodeURIComponent(lotCustomerId)}`
                        : `${process.env.REACT_APP_BACKEND_URL}/api/dispatch-lots-v2/sku-search?customer_id=__none__`
                    }
                    disabled={!lotCustomerId}
                    value={addLineData.buyer_sku_id}
                    onSelect={(item) => handleAddLineLookup(item.item_id)}
                    placeholder={lotCustomerId ? "Type SKU ID or name..." : "Lot has no customer set"}
                    testId="add-line-sku-search"
                  />
                );
              })()}
            </div>
            
            {skuLookup && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                <p><strong>Name:</strong> {skuLookup.name}</p>
                <p><strong>HSN:</strong> {skuLookup.hsn_code || "Not set"}</p>
                <p><strong>GST:</strong> {skuLookup.gst_rate}%</p>
                <p><strong>Rate:</strong> {skuLookup.rate > 0 ? `₹${skuLookup.rate.toLocaleString()}` : "Not set in Price Master"}</p>
              </div>
            )}
            
            <div>
              <Label>Quantity *</Label>
              <Input 
                type="number" 
                min="1"
                value={addLineData.quantity}
                onChange={(e) => setAddLineData({ ...addLineData, quantity: parseInt(e.target.value) || 1 })}
                {...withNumericInputProps()}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddLineDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddLineSubmit} disabled={addingLine || !addLineData.buyer_sku_id}>
                {addingLine ? "Adding..." : "Add Line"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Lot dialog (Feb 2026) — header + line items + Add Line CTA */}
      <BookVehicleDialog
        open={bookVehicleOpen}
        onClose={() => setBookVehicleOpen(false)}
        sourceType="DISPATCH_LOT"
        sourceId={viewLot?.id}
        sourceLabel={viewLot ? `Lot ${viewLot.lot_number}` : ""}
      />

      <Dialog open={showViewDialog} onOpenChange={(o) => { setShowViewDialog(o); if (!o) { setViewLot(null); setEditedLines([]); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lot-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase flex items-center gap-2">
              <Eye className="h-5 w-5" />
              {viewLot?.lot_number || "Dispatch Lot"}
              {viewLot?.status && (
                <Badge className={STATUS_COLORS[viewLot.status]}>{STATUS_LABELS[viewLot.status] || viewLot.status}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewLot && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Customer</Label>
                  <div data-testid="lot-view-customer">{viewLot.customer_name || "—"}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Customer ID</Label>
                  <div className="font-mono text-xs">{viewLot.customer_id || "—"}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Total Quantity</Label>
                  <div className="font-mono" data-testid="lot-view-total-qty">{viewLot.total_quantity ?? 0}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Target Dispatch Completion</Label>
                  <div data-testid="lot-view-target-dispatch">
                    <TargetDispatchCell lot={viewLot} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Availability</Label>
                  <div data-testid="lot-view-availability">
                    <AvailabilityDot status={viewLot.availability_status} />
                  </div>
                </div>
                {viewLot.notes && (
                  <div className="col-span-2">
                    <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
                    <div className="text-sm">{viewLot.notes}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">
                    Line items ({(viewLot.lines || []).length})
                  </Label>
                  <div className="flex items-center gap-2">
                  <BookVehicleButton onClick={() => setBookVehicleOpen(true)} />
                  {(viewLot.status === "DRAFT" || viewLot.status === "PENDING_FINANCE") && (isFinanceTeam || isDemandTeam) && (
                    <Button
                      size="sm"
                      onClick={() => {
                        // AC4 guard — Add Line must not silently drop the
                        // user's in-progress qty/delete edits.
                        if (isViewLotDirty) {
                          toast.error("Save or discard your changes before adding a new line.");
                          return;
                        }
                        setShowViewDialog(false);
                        openAddLineDialog(viewLot);
                      }}
                      data-testid="lot-view-add-line-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Line
                    </Button>
                  )}
                  </div>
                </div>
                <div className="border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50">
                        <TableHead className="w-40">Buyer SKU</TableHead>
                        <TableHead>SKU Name</TableHead>
                        <TableHead className="w-24 text-right">Qty</TableHead>
                        <TableHead className="w-20 text-right">Available</TableHead>
                        <TableHead className="w-24">Availability</TableHead>
                        <TableHead className="w-32">Target Date</TableHead>
                        {/* Feb 2026 R14+ (AC7) — per-line forecast status
                            + linked forecast_code for Linked lines. */}
                        <TableHead className="w-40" data-testid="lot-line-forecast-col">Forecast</TableHead>
                        <TableHead className="w-24 text-right">Rate</TableHead>
                        <TableHead className="w-28 text-right">Amount</TableHead>
                        {isDraftEditable && <TableHead className="w-12 text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(isDraftEditable ? editedLines : (viewLot.lines || [])).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isDraftEditable ? 10 : 9} className="text-center text-muted-foreground py-6">
                            No line items yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        (isDraftEditable ? editedLines : (viewLot.lines || [])).map((ln, idx) => (
                          <TableRow key={ln.id || `${ln.buyer_sku_id}-${idx}`} data-testid={`lot-view-line-${idx}`}>
                            <TableCell className="font-mono text-xs">{ln.buyer_sku_id || "—"}</TableCell>
                            <TableCell className="text-sm">{ln.sku_name || "—"}</TableCell>
                            <TableCell className="text-right font-mono">
                              {isDraftEditable ? (
                                <Input
                                  type="number"
                                  min="1"
                                  value={ln.quantity ?? 1}
                                  onChange={(e) => handleUpdateLineQty(idx, e.target.value)}
                                  {...withNumericInputProps("h-8 w-20 text-right ml-auto")}
                                  data-testid={`lot-view-line-${idx}-qty-input`}
                                />
                              ) : (
                                (ln.quantity ?? "—")
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono" data-testid={`lot-view-line-${idx}-available`}>
                              {ln.available ?? "—"}
                            </TableCell>
                            <TableCell data-testid={`lot-view-line-${idx}-availability`}>
                              <AvailabilityDot status={ln.availability_status} />
                            </TableCell>
                            <TableCell className="text-xs" data-testid={`lot-view-line-${idx}-target`}>
                              <LineTargetDate contribution={ln.target_date_contribution} />
                            </TableCell>
                            <TableCell className="text-xs" data-testid={`lot-view-line-${idx}-forecast`}>
                              <div className="flex flex-col gap-0.5">
                                <ForecastStatusBadge status={ln.forecast_status} />
                                {ln.forecast_status === "LINKED" && ln.linked_forecast_code && (
                                  <span className="font-mono text-[10px] text-zinc-500" data-testid={`lot-view-line-${idx}-forecast-id`}>
                                    {ln.linked_forecast_code}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">{ln.rate ?? "—"}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{ln.amount ?? "—"}</TableCell>
                            {isDraftEditable && (
                              <TableCell className="text-right">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleDeleteLine(idx)}
                                  disabled={editedLines.length <= 1}
                                  title={editedLines.length <= 1
                                    ? "A lot must have at least one line item — delete the whole lot from the list instead."
                                    : "Remove this line"}
                                  data-testid={`lot-view-line-${idx}-delete-btn`}
                                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                {isDraftEditable && isViewLotDirty && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleDiscardLotEdits}
                      disabled={savingLotEdits}
                      data-testid="lot-view-discard-changes-btn"
                    >
                      Discard
                    </Button>
                    <Button
                      onClick={handleSaveLotEdits}
                      disabled={savingLotEdits || editedLines.length === 0}
                      data-testid="lot-view-save-changes-btn"
                    >
                      {savingLotEdits ? "Saving..." : "Save Changes"}
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setShowViewDialog(false)} data-testid="lot-view-close">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Send-to-Finance capture dialog (Demand/KAM). */}
      <SendToFinanceDialog
        open={showSendToFinance}
        onOpenChange={setShowSendToFinance}
        lot={sendToFinanceLot}
        branches={branches}
        onSent={handleSendToFinanceSent}
      />

      {/* Feb 2026 — Read-only viewer of what was submitted to Finance. */}
      <LotSubmittedViewerDialog
        open={showSubmittedViewer}
        onClose={() => { setShowSubmittedViewer(false); setSubmittedViewerLot(null); }}
        lot={submittedViewerLot}
      />
    </div>
  );
};

export default DispatchLots;
